import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Section, Loading, DataTable, StatusPill } from "../components/Common";
import { FileText, Search, Download, Filter } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { v: "", label: "All types" },
  { v: "student_report", label: "Student Reports" },
  { v: "board_pack", label: "Board Packs" },
];

export default function ReportArchive() {
  const [docs, setDocs] = useState(null);
  const [type, setType] = useState("");
  const [q, setQ] = useState("");

  const load = () => {
    setDocs(null);
    api.archive({ document_type: type || undefined }).then(({ data }) => setDocs(data.documents || []));
  };

  useEffect(() => { load(); }, [type]);

  if (docs === null) return <Loading label="Loading academic records repository…" />;

  const filtered = q
    ? docs.filter((d) => (d.subject_label || "").toLowerCase().includes(q.toLowerCase()))
    : docs;

  const openPdf = async (id) => {
    const token = localStorage.getItem("edu360.token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/education360/publishing/archive/${id}/pdf`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Unable to open PDF"); return; }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    window.open(objectUrl, "_blank");
  };

  return (
    <div data-testid="archive-page">
      <PageHeader
        kicker="Academic Records Repository"
        title="Report Archive"
        subtitle="Every published report is retained here with version history, QR verification and full audit trail — retrievable for parents, boards and inspectors."
      />

      <div className="hl-card p-4 mb-6 flex flex-wrap gap-3 items-center" data-testid="archive-filters">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by student or subject label…"
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-md"
            data-testid="archive-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          {TYPES.map((t) => (
            <button
              key={t.v}
              onClick={() => setType(t.v)}
              data-testid={`archive-type-${t.v || "all"}`}
              className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-widest border ${type === t.v ? "bg-[color:var(--hl-navy)] text-white border-[color:var(--hl-navy)]" : "bg-white text-slate-600 border-slate-200 hover:border-[color:var(--hl-orange)]"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <Section testId="archive-list">
        <DataTable
          testId="archive-table"
          columns={[
            { key: "subject_label", label: "Document", render: (r) => (
              <div>
                <div className="font-semibold text-slate-800">{r.subject_label}</div>
                <div className="text-[11px] text-slate-500 mono">{r.verification_id}</div>
              </div>
            ) },
            { key: "document_type", label: "Type", render: (r) => (
              <StatusPill status={r.document_type === "board_pack" ? "info" : "active"} />
            ) },
            { key: "period", label: "Period" },
            { key: "academic_year", label: "Academic Year" },
            { key: "version", label: "Version", render: (r) => `v${r.version}` },
            { key: "size_bytes", label: "Size", render: (r) => `${((r.size_bytes || 0) / 1024).toFixed(0)} KB` },
            { key: "created_at", label: "Published", render: (r) => (r.created_at || "").slice(0, 16) },
            { key: "actions", label: "PDF", render: (r) => (
              <button
                onClick={() => openPdf(r.id)}
                className="text-[color:var(--hl-orange)] hover:underline inline-flex items-center gap-1 text-xs font-semibold"
                data-testid={`archive-open-${r.id}`}
              >
                <Download className="w-3 h-3" /> Open
              </button>
            ) },
          ]}
          rows={filtered}
          empty="No published documents yet. Publish a report or a Board Pack to see it here."
        />
      </Section>
    </div>
  );
}
